// Intent: [Conversion] [Journey] [SEO/GEO]
import React from "react";
import { CheckIcon, HammerIcon, HardHatIcon, RulerIcon } from "lucide-react";
import { ButtonLink } from "../shared/ButtonLink";
import { SectionHeading } from "../shared/SectionHeading";
const audiences = [{
  label: "Trades / Cabinet Installers",
  title: "Package jobs clearly and keep installation work moving.",
  copy: "For installers and trades who already understand cabinetry requirements and want a more direct ordering path.",
  bullets: ["Prepare final dimensions", "Keep job information together", "Review selections before submission", "Ask for specification clarification"],
  cta: "Explore Trade Ordering",
  href: "/who-we-help/#trades",
  analytics: "trade_account_clicked",
  icon: HammerIcon,
  theme: "navy"
}, {
  label: "Builders",
  title: "Create a repeatable cabinetry path for project work.",
  copy: "For builders coordinating plans, rooms, customer selections and project information across builds and renovations.",
  bullets: ["Prepare plans and schedules", "Organise requirements by project", "Keep selections visible", "Confirm the next step before commitment"],
  cta: "Explore Builder Ordering",
  href: "/who-we-help/#builders",
  analytics: "builder_job_clicked",
  icon: HardHatIcon,
  theme: "steel"
}, {
  label: "DIY Renovators",
  title: "Prepare carefully before configuring cabinets.",
  copy: "For capable renovators comfortable taking accurate measurements and making informed project selections.",
  bullets: ["Start with the measurement checklist", "Gather appliance specifications", "Check clearances and openings", "Ask Austen before submission"],
  cta: "Prepare a DIY Project",
  href: "/who-we-help/#diy",
  analytics: "diy_project_clicked",
  icon: RulerIcon,
  theme: "orange"
}] as const;
function AudiencePaths() {
  return <section id="who-we-help" className="section-shell bg-[#eceae4]" aria-labelledby="audience-title" data-id="element-116" data-mp="cxhg6g612v5l">
      <div className="container-shell" data-id="element-117" data-mp="7y4vo22mq066">
        <SectionHeading id="audience-title" eyebrow="Choose your pathway" title="Choose the path that matches your project." copy="Different projects need different preparation. Start with the route that fits how you work." data-id="element-118" data-mp="7z65623zyels" />
        <div className="mt-10 grid gap-5 lg:grid-cols-3" data-id="element-119" data-mp="277ej4thxfsln">
          {audiences.map(({
          label,
          title,
          copy,
          bullets,
          cta,
          href,
          analytics,
          icon: Icon,
          theme
        }) => {
          const dark = theme !== "orange";
          const bg = theme === "navy" ? "bg-pfpc-navy" : theme === "steel" ? "bg-pfpc-steel" : "bg-pfpc-offwhite";
          return <article key={label} className={`relative flex min-h-[460px] flex-col overflow-hidden rounded-xl border-t-4 ${bg} p-7 lg:min-h-[510px] ${theme === "orange" ? "border-pfpc-orange outline outline-1 outline-pfpc-navy/12" : theme === "steel" ? "border-[#80a0aa]" : "border-pfpc-orange"}`} data-id="element-120" data-mp="2q3eco8qjz47c">
                <div className={`absolute -right-9 top-16 h-36 w-36 border ${dark ? "border-pfpc-offwhite/10" : "border-pfpc-orange/20"}`} data-id="element-121" data-mp="10og94gl6l921"><div className="absolute inset-5 border border-current opacity-30" data-id="element-122" data-mp="14snymfzgqt1b" /></div>
                <div className={`relative grid h-12 w-12 place-items-center rounded-[10px] ${dark ? "bg-pfpc-offwhite/10 text-pfpc-orange" : "bg-pfpc-orange/15 text-pfpc-orange"}`} data-id="element-123" data-mp="x60ofixxyt6x"><Icon className="h-6 w-6" data-id="element-124" data-mp="176b192g2dylk" /></div>
                <p className={`section-label relative mt-7 ${dark ? "text-pfpc-offwhite/60" : "text-pfpc-steel"}`} data-id="element-125" data-mp="174zxh2mmcqug">{label}</p>
                <h3 className={`relative mt-3 font-heading text-[23px] font-extrabold leading-[1.25] ${dark ? "text-pfpc-offwhite" : "text-pfpc-navy"}`} data-id="element-126" data-mp="eh3trdymtyo6">{title}</h3>
                <p className={`relative mt-4 text-[16px] leading-7 ${dark ? "text-pfpc-offwhite/72" : "text-pfpc-navy/72"}`} data-id="element-127" data-mp="9jkdk8idjbtl">{copy}</p>
                <ul className="relative my-7 space-y-2.5" data-id="element-128" data-mp="jg71x79wsl0d">
                  {bullets.map(bullet => <li key={bullet} className={`flex gap-2 text-[15px] font-semibold leading-6 ${dark ? "text-pfpc-offwhite/82" : "text-pfpc-navy/78"}`} data-id="element-129" data-mp="jecd3hvio8r0"><CheckIcon className="mt-1 h-4 w-4 shrink-0 text-pfpc-orange" strokeWidth={3} data-id="element-130" data-mp="3mxfhfkh5z8rx" />{bullet}</li>)}
                </ul>
                <ButtonLink href={href} variant={dark ? "secondaryDark" : "primary"} analytics={analytics} className="relative mt-auto w-full" data-id="element-131" data-mp="2q6tv6tg5gbju">{cta}</ButtonLink>
              </article>;
        })}
        </div>
      </div>
    </section>;
}
export { AudiencePaths };